import { useMemo } from 'react';
import { trpc } from '../../lib/trpc';
import { Loader2 } from 'lucide-react';
import {
  Building,
  Users,
  LayoutGrid,
  TrendingUp,
  Home,
  Shield,
  Building2,
  CheckCircle2,
  Clock,
  AlertCircle,
  MapPin,
  Star,
  Zap,
  BarChart3,
  UserPlus,
  FileText,
} from 'lucide-react';

// Soft UI Progress Bar Component
function SoftProgressBar({ 
  value, 
  max = 100, 
  color = 'mustard',
  size = 'md',
  showValue = true 
}: { 
  value: number; 
  max?: number; 
  color?: 'mustard' | 'charcoal' | 'emerald' | 'rose';
  size?: 'sm' | 'md' | 'lg';
  showValue?: boolean;
}) {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));
  
  const colorClasses = {
    mustard: 'bg-gradient-to-r from-[var(--mustard)] to-[#E8C547]',
    charcoal: 'bg-gradient-to-r from-[var(--charcoal)] to-[var(--charcoal-light)]',
    emerald: 'bg-gradient-to-r from-emerald-400 to-emerald-500',
    rose: 'bg-gradient-to-r from-rose-400 to-rose-500',
  };
  
  const sizeClasses = {
    sm: 'h-2',
    md: 'h-3',
    lg: 'h-4',
  };

  return (
    <div className="flex items-center gap-3">
      <div className={`flex-1 ${sizeClasses[size]} rounded-full bg-[var(--cream-dark)] overflow-hidden`}>
        <div 
          className={`h-full rounded-full ${colorClasses[color]} transition-all duration-700 ease-out`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {showValue && (
        <span className="text-sm font-semibold text-[var(--charcoal)] min-w-[3rem] text-right">
          {Math.round(percentage)}%
        </span>
      )}
    </div>
  );
}

// Soft UI Stat Card
function BentoStatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  color = 'mustard',
  size = 'normal',
  isLoading,
}: {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ElementType;
  trend?: { value: number; isPositive: boolean };
  color?: 'mustard' | 'charcoal' | 'cream' | 'emerald' | 'rose';
  size?: 'normal' | 'large' | 'wide';
  isLoading: boolean;
}) {
  const colorStyles = {
    mustard: 'bg-gradient-to-br from-[var(--mustard)] to-[#E8C547] text-white',
    charcoal: 'bg-gradient-to-br from-[var(--charcoal)] to-[var(--charcoal-light)] text-white',
    cream: 'bg-[var(--card)] text-[var(--charcoal)]',
    emerald: 'bg-gradient-to-br from-emerald-400 to-emerald-500 text-white',
    rose: 'bg-gradient-to-br from-rose-400 to-rose-500 text-white',
  };

  const isDark = color !== 'cream';

  return (
    <div 
      className={`
        relative overflow-hidden rounded-[1.75rem] p-6 
        ${colorStyles[color]}
        ${size === 'large' ? 'row-span-2' : ''}
        ${size === 'wide' ? 'col-span-2' : ''}
        shadow-[var(--card-shadow)]
        transition-all duration-300 hover:shadow-[var(--card-shadow-hover)] hover:-translate-y-1
      `}
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
      
      <div className="relative">
        <div className="flex items-start justify-between mb-4">
          <div className={`p-3 rounded-2xl ${isDark ? 'bg-white/20' : 'bg-[var(--mustard)]/10'}`}>
            <Icon className={`h-6 w-6 ${isDark ? 'text-white' : 'text-[var(--mustard)]'}`} />
          </div>
          {trend && (
            <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ${
              isDark ? 'bg-white/20' : 'bg-[var(--cream-dark)]'
            }`}>
              <TrendingUp className={`h-3 w-3 ${trend.isPositive ? '' : 'rotate-180'}`} />
              {trend.value}%
            </div>
          )}
        </div>

        {isLoading ? (
          <Loader2 className={`h-8 w-8 animate-spin ${isDark ? 'text-white/60' : 'text-[var(--muted-foreground)]'}`} />
        ) : (
          <>
            <div className={`text-5xl font-light tracking-tight mb-2 ${size === 'large' ? 'text-6xl' : ''}`}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </div>
            <div className={`text-sm font-medium ${isDark ? 'text-white/80' : 'text-[var(--warm-gray)]'}`}>
              {title}
            </div>
            {subtitle && (
              <div className={`text-xs mt-1 ${isDark ? 'text-white/60' : 'text-[var(--muted-foreground)]'}`}>
                {subtitle}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Building Rank Card
function BuildingRankCard({
  buildings,
  isLoading,
}: {
  buildings: Array<{
    id: string;
    name: string;
    code: string;
    unitCount: number;
    residentCount: number;
  }>;
  isLoading: boolean;
}) {
  return (
    <div className="bg-[var(--card)] rounded-[1.75rem] p-6 shadow-[var(--card-shadow)] h-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-[var(--charcoal)]">Top Buildings</h3>
          <p className="text-sm text-[var(--warm-gray)]">By unit count</p>
        </div>
        <div className="p-2.5 rounded-xl bg-[var(--mustard)]/10">
          <Building2 className="h-5 w-5 text-[var(--mustard)]" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--mustard)]" />
        </div>
      ) : buildings.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-[var(--warm-gray)]">
          <Building2 className="h-12 w-12 mb-3 opacity-20" />
          <p className="text-sm">No buildings found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {buildings.map((building, index) => (
            <div 
              key={building.id}
              className="flex items-center gap-4 p-3 rounded-2xl bg-[var(--cream)] hover:bg-[var(--cream-dark)] transition-colors group"
            >
              <div className={`
                flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold
                ${index === 0 ? 'bg-[var(--mustard)] text-white' : 
                  index === 1 ? 'bg-[var(--charcoal)] text-white' : 
                  index === 2 ? 'bg-[var(--warm-gray)] text-white' : 
                  'bg-[var(--cream-dark)] text-[var(--charcoal-light)]'}
              `}>
                {index + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[var(--charcoal)] truncate">{building.name}</p>
                <p className="text-xs text-[var(--warm-gray)] flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {building.code}
                </p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-[var(--charcoal)]">{building.unitCount}</p>
                <p className="text-xs text-[var(--warm-gray)]">units</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Recent Residents Card
function RecentResidentsCard({
  residents,
  isLoading,
}: {
  residents: Array<{
    id: string;
    name?: string;
    username?: string;
    status?: string;
    unit_owners?: Array<{ units?: { unit_no?: string } }>;
  }>;
  isLoading: boolean;
}) {
  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-700';
      case 'suspended': return 'bg-rose-100 text-rose-700';
      default: return 'bg-amber-100 text-amber-700';
    }
  };

  return (
    <div className="bg-[var(--card)] rounded-[1.75rem] p-6 shadow-[var(--card-shadow)] h-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-[var(--charcoal)]">Recent Residents</h3>
          <p className="text-sm text-[var(--warm-gray)]">Latest additions</p>
        </div>
        <div className="p-2.5 rounded-xl bg-[var(--mustard)]/10">
          <Users className="h-5 w-5 text-[var(--mustard)]" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--mustard)]" />
        </div>
      ) : residents.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-[var(--warm-gray)]">
          <Users className="h-12 w-12 mb-3 opacity-20" />
          <p className="text-sm">No residents found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {residents.map((resident) => (
            <div 
              key={resident.id}
              className="flex items-center gap-4 p-3 rounded-2xl bg-[var(--cream)] hover:bg-[var(--cream-dark)] transition-colors"
            >
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[var(--mustard)]/20 to-[var(--mustard)]/5 flex items-center justify-center">
                <span className="text-sm font-bold text-[var(--mustard)]">
                  {resident.name?.charAt(0).toUpperCase() || '?'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[var(--charcoal)] truncate">{resident.name}</p>
                <p className="text-xs text-[var(--warm-gray)]">{resident.username}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(resident.status)}`}>
                {resident.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Activity Overview Card
function ActivityOverviewCard({
  activeResidents,
  suspendedResidents,
  pendingResidents,
  totalResidents,
  isLoading,
}: {
  activeResidents: number;
  suspendedResidents: number;
  pendingResidents: number;
  totalResidents: number;
  isLoading: boolean;
}) {
  const data = [
    { label: 'Active', value: activeResidents, color: 'bg-emerald-400', icon: CheckCircle2 },
    { label: 'Pending', value: pendingResidents, color: 'bg-amber-400', icon: Clock },
    { label: 'Suspended', value: suspendedResidents, color: 'bg-rose-400', icon: AlertCircle },
  ];

  return (
    <div className="bg-[var(--charcoal)] rounded-[1.75rem] p-6 text-white shadow-xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold">Resident Status</h3>
          <p className="text-sm text-white/60">Current distribution</p>
        </div>
        <div className="p-2.5 rounded-xl bg-white/10">
          <BarChart3 className="h-5 w-5 text-white" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-8 w-8 animate-spin text-white/60" />
        </div>
      ) : (
        <>
          <div className="flex gap-1 h-4 rounded-full overflow-hidden mb-6">
            {data.map((item) => (
              <div 
                key={item.label}
                className={`${item.color} transition-all duration-500`}
                style={{ width: totalResidents > 0 ? `${(item.value / totalResidents) * 100}%` : '0%' }}
              />
            ))}
          </div>

          <div className="space-y-3">
            {data.map((item) => (
              <div key={item.label} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-xl ${item.color} bg-opacity-20`}>
                    <item.icon className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-sm text-white/80">{item.label}</span>
                </div>
                <span className="font-semibold">{item.value}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Quick Action Card
function QuickActionCard() {
  const actions = [
    { label: 'Add Resident', icon: UserPlus, color: 'bg-emerald-100 text-emerald-600', href: '/admin/residents' },
    { label: 'Add Building', icon: Building, color: 'bg-blue-100 text-blue-600', href: '/admin/buildings' },
    { label: 'View Reports', icon: FileText, color: 'bg-purple-100 text-purple-600', href: '#' },
  ];

  return (
    <div className="bg-[var(--card)] rounded-[1.75rem] p-6 shadow-[var(--card-shadow)]">
      <h3 className="text-lg font-semibold text-[var(--charcoal)] mb-4">Quick Actions</h3>
      <div className="grid grid-cols-3 gap-3">
        {actions.map((action) => (
          <a
            key={action.label}
            href={action.href}
            className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-[var(--cream)] hover:bg-[var(--cream-dark)] transition-colors group"
          >
            <div className={`p-3 rounded-xl ${action.color} transition-transform group-hover:scale-110`}>
              <action.icon className="h-5 w-5" />
            </div>
            <span className="text-xs font-medium text-[var(--charcoal-light)]">{action.label}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

export function AdminDashboardPage() {
  const { data: buildings, isLoading: buildingsLoading } = trpc.buildings.list.useQuery();
  const { data: residents, isLoading: residentsLoading } = trpc.admin.listResidents.useQuery({});
  const { data: units, isLoading: unitsLoading } = trpc.buildings.listUnits.useQuery();
  const { data: organizations, isLoading: orgsLoading } = trpc.organizations.list.useQuery();
  const { data: buildingAdmins, isLoading: adminsLoading } = trpc.admin.listBuildingAdmins.useQuery();

  const isLoading = buildingsLoading || residentsLoading || unitsLoading || orgsLoading || adminsLoading;

  const stats = useMemo(() => {
    const buildingCount = buildings?.length ?? 0;
    const residentCount = residents?.length ?? 0;
    const unitCount = units?.length ?? 0;
    const orgCount = organizations?.length ?? 0;
    const adminCount = buildingAdmins?.length ?? 0;

    const occupiedUnits = units?.filter((u: { status?: string; owner_name?: string }) => 
      u.status === 'occupied' || u.owner_name
    ).length ?? 0;
    const occupancyRate = unitCount > 0 ? Math.round((occupiedUnits / unitCount) * 100) : 0;

    const activeResidents = residents?.filter((r: { status?: string }) => r.status === 'active').length ?? 0;
    const suspendedResidents = residents?.filter((r: { status?: string }) => r.status === 'suspended').length ?? 0;
    const pendingResidents = residents?.filter((r: { status?: string }) => r.status === 'pending').length ?? 0;

    const recentResidents = residents?.slice(0, 5) ?? [];

    const buildingsWithUnitCount = buildings?.map((b: { id: string; name: string; code: string }) => ({
      ...b,
      unitCount: units?.filter((u: { building_id: string }) => u.building_id === b.id).length ?? 0,
      residentCount: residents?.filter((r: { unit_owners?: Array<{ units?: { building_id: string } }> }) => 
        r.unit_owners?.some((uo) => uo.units?.building_id === b.id)
      ).length ?? 0
    })).sort((a: { unitCount: number }, b: { unitCount: number }) => b.unitCount - a.unitCount).slice(0, 5) ?? [];

    return {
      buildingCount,
      residentCount,
      unitCount,
      orgCount,
      adminCount,
      occupiedUnits,
      occupancyRate,
      activeResidents,
      suspendedResidents,
      pendingResidents,
      recentResidents,
      buildingsWithUnitCount,
    };
  }, [buildings, residents, units, organizations, buildingAdmins]);

  return (
    <div className="space-y-8 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-light text-[var(--charcoal)] tracking-tight mb-1">
            Dashboard
          </h1>
          <p className="text-[var(--warm-gray)]">
            Welcome back! Here's what's happening with your properties.
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--card)] shadow-[var(--card-shadow)]">
          <Zap className="h-4 w-4 text-[var(--mustard)]" />
          <span className="text-sm font-medium text-[var(--charcoal)]">
            {stats.occupancyRate}% Occupancy
          </span>
        </div>
      </div>

      {/* Bento Grid - Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <BentoStatCard
          title="Total Buildings"
          value={stats.buildingCount}
          subtitle={`Across ${stats.orgCount} organizations`}
          icon={Building}
          trend={{ value: 12, isPositive: true }}
          color="mustard"
          isLoading={isLoading}
        />
        
        <BentoStatCard
          title="Total Units"
          value={stats.unitCount}
          subtitle={`${stats.occupiedUnits} occupied`}
          icon={LayoutGrid}
          color="charcoal"
          isLoading={isLoading}
        />
        
        <BentoStatCard
          title="Total Residents"
          value={stats.residentCount}
          subtitle={`${stats.activeResidents} active`}
          icon={Users}
          trend={{ value: 8, isPositive: true }}
          color="cream"
          isLoading={isLoading}
        />
        
        <BentoStatCard
          title="Building Admins"
          value={stats.adminCount}
          subtitle="Managing properties"
          icon={Shield}
          color="cream"
          isLoading={isLoading}
        />
      </div>

      {/* Secondary Stats with Progress Bars */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-[var(--card)] rounded-[1.75rem] p-6 shadow-[var(--card-shadow)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-xl bg-emerald-100">
              <Home className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-[var(--warm-gray)]">Occupancy Rate</p>
              <p className="text-2xl font-light text-[var(--charcoal)]">{stats.occupancyRate}%</p>
            </div>
          </div>
          <SoftProgressBar value={stats.occupiedUnits} max={stats.unitCount} color="mustard" />
        </div>

        <div className="bg-[var(--card)] rounded-[1.75rem] p-6 shadow-[var(--card-shadow)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-xl bg-blue-100">
              <Star className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-[var(--warm-gray)]">Active Rate</p>
              <p className="text-2xl font-light text-[var(--charcoal)]">
                {stats.residentCount > 0 ? Math.round((stats.activeResidents / stats.residentCount) * 100) : 0}%
              </p>
            </div>
          </div>
          <SoftProgressBar 
            value={stats.activeResidents} 
            max={stats.residentCount} 
            color="charcoal" 
          />
        </div>

        <div className="bg-[var(--card)] rounded-[1.75rem] p-6 shadow-[var(--card-shadow)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-xl bg-purple-100">
              <TrendingUp className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-[var(--warm-gray)]">Units per Building</p>
              <p className="text-2xl font-light text-[var(--charcoal)]">
                {stats.buildingCount > 0 ? Math.round(stats.unitCount / stats.buildingCount) : 0}
              </p>
            </div>
          </div>
          <SoftProgressBar 
            value={stats.unitCount} 
            max={stats.buildingCount * 50} 
            color="mustard" 
          />
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Buildings - Takes 2 columns */}
        <div className="lg:col-span-2">
          <BuildingRankCard 
            buildings={stats.buildingsWithUnitCount} 
            isLoading={isLoading} 
          />
        </div>

        {/* Activity Overview */}
        <div>
          <ActivityOverviewCard
            activeResidents={stats.activeResidents}
            suspendedResidents={stats.suspendedResidents}
            pendingResidents={stats.pendingResidents}
            totalResidents={stats.residentCount}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Residents - Takes 2 columns */}
        <div className="lg:col-span-2">
          <RecentResidentsCard 
            residents={stats.recentResidents} 
            isLoading={isLoading} 
          />
        </div>

        {/* Quick Actions */}
        <div>
          <QuickActionCard />
        </div>
      </div>
    </div>
  );
}
